// grimdark/client/src/components/UI/Huds/grimdarkHudUtils.js

// ---------- small helpers ----------
export const clampLines = (arr, max) => {
  if (arr.length <= max) return arr;
  return arr.slice(arr.length - max);
};

export const makePickLeft = (rites) => () =>
  rites[(Math.random() * rites.length) | 0];

export const makePickRight = () => () => {
  const hexA = Math.random().toString(16).slice(2, 10).toUpperCase();
  const hexB = Math.random().toString(16).slice(2, 10).toUpperCase();
  return `>> ${hexA} :: ${hexB}`;
};

// ---------- text controller ----------
export function createHudTextController({
  setLeftLines,
  setRightLines,
  pickLeft,
  pickRight,
  maxLeftLines,
  maxRightLines,
  rightIntervalMs = 55,
}) {
  const s = {
    typingActive: false,
    leftCharTimer: null,
    leftNextLineTimer: null,
    rightStreamTimer: null,
    startTextTimer: null,
  };

  const clearLeft = () => {
    if (s.leftCharTimer) clearTimeout(s.leftCharTimer);
    if (s.leftNextLineTimer) clearTimeout(s.leftNextLineTimer);
    s.leftCharTimer = null;
    s.leftNextLineTimer = null;
  };

  const clearRight = () => {
    if (s.rightStreamTimer) clearInterval(s.rightStreamTimer);
    s.rightStreamTimer = null;
  };

  const stop = () => {
    s.typingActive = false;
    clearLeft();
    clearRight();
    if (s.startTextTimer) clearTimeout(s.startTextTimer);
    s.startTextTimer = null;
  };

  const typeOneLeftLine = (fullText, onDone) => {
    setLeftLines((prev) => clampLines([...prev, ''], maxLeftLines));

    let idx = 0;

    const step = () => {
      if (!s.typingActive) return;

      idx += 1;
      setLeftLines((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        next[next.length - 1] = fullText.slice(0, idx);
        return next;
      });

      if (idx >= fullText.length) {
        s.leftCharTimer = setTimeout(() => {
          if (!s.typingActive) return;
          onDone?.();
        }, 60);
        return;
      }

      const nextDelay = 18 + Math.random() * 42;
      s.leftCharTimer = setTimeout(step, nextDelay);
    };

    step();
  };

  const startNow = () => {
    s.typingActive = true;
    setLeftLines([]);
    setRightLines([]);

    const leftLoop = () => {
      if (!s.typingActive) return;

      const line = pickLeft();
      typeOneLeftLine(line, () => {
        if (!s.typingActive) return;
        const nextDelay = 220 + Math.random() * 520;
        s.leftNextLineTimer = setTimeout(leftLoop, nextDelay);
      });
    };

    leftLoop();

    s.rightStreamTimer = setInterval(() => {
      if (!s.typingActive) return;

      setRightLines((prev) =>
        clampLines([...prev, pickRight()], maxRightLines)
      );
    }, rightIntervalMs);
  };

  const startAfter = (ms) => {
    stop();
    s.startTextTimer = setTimeout(startNow, ms);
  };

  return { startAfter, stop };
}

// ---------- noise canvas controller ----------
export function createNoiseController({
  canvasRef,
  raiseMs,
  frameDelayMs = 120,
  tickMs = 70,
}) {
  const s = { raf: null, timeout: null, running: false };

  const resizeToDpr = () => {
    const c = canvasRef.current;
    if (!c) return;

    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.max(1, Math.round(rect.width * dpr));
    c.height = Math.max(1, Math.round(rect.height * dpr));
  };

  const start = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;

    s.running = true;

    const draw = () => {
      if (!s.running) return;

      const w = c.width;
      const h = c.height;

      const img = ctx.createImageData(w, h);
      const d = img.data;

      for (let i = 0; i < d.length; i += 4) {
        let v = (Math.random() * 255) | 0;
        v = (v * v) / 255;
        if (Math.random() < 0.012) v = 255;

        d[i] = v;
        d[i + 1] = v;
        d[i + 2] = v;
        d[i + 3] = 255;
      }

      ctx.putImageData(img, 0, 0);

      // throttle a bit so it feels analog
      s.raf = window.setTimeout(() => {
        s.raf = requestAnimationFrame(draw);
      }, tickMs);
    };

    draw();
  };

  const stop = () => {
    s.running = false;

    if (s.raf) {
      cancelAnimationFrame(s.raf);
      clearTimeout(s.raf);
      s.raf = null;
    }
    if (s.timeout) {
      clearTimeout(s.timeout);
      s.timeout = null;
    }
  };

  const startAfterRaise = () => {
    stop();
    s.timeout = window.setTimeout(start, raiseMs + frameDelayMs);
  };

  return { resizeToDpr, startAfterRaise, stop };
}
